import * as PopoverPrimitive from "@radix-ui/react-popover";
import { Command } from "cmdk";
import { useId, useState, type ReactNode } from "react";
import { cn } from "../lib/cn";
import { Icon } from "./icon";

export interface ComboboxOption<T extends string = string> {
  value: T;
  label: string;
  description?: string;
  /** Extra search terms. */
  keywords?: readonly string[];
}

export interface ComboboxLabels {
  placeholder: string;
  search: string;
  empty: string;
}

const DEFAULT_LABELS: ComboboxLabels = { placeholder: "Select…", search: "Search…", empty: "No matches" };

interface PickerProps<T extends string> {
  options: readonly ComboboxOption<T>[];
  /** Accessible name of the trigger (or wire with Field via `id`). */
  "aria-label"?: string;
  id?: string;
  labels?: Partial<ComboboxLabels>;
  disabled?: boolean;
  className?: string;
}

const TRIGGER_CLASS = "input flex cursor-pointer items-center justify-between gap-2 text-left disabled:cursor-not-allowed disabled:opacity-50";
const ITEM_CLASS =
  "flex cursor-pointer items-center gap-2 rounded-md px-2.5 py-2 text-sm text-fg data-[selected=true]:bg-sunken data-[disabled=true]:opacity-50";

interface PickerShellProps {
  id?: string;
  ariaLabel?: string;
  disabled?: boolean;
  className?: string;
  display: ReactNode;
  labels: ComboboxLabels;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  children: ReactNode;
}

function PickerShell({ id, ariaLabel, disabled, className, display, labels, open, onOpenChange, children }: PickerShellProps) {
  const listId = useId();
  return (
    <PopoverPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <PopoverPrimitive.Trigger asChild>
        <button
          type="button"
          id={id}
          role="combobox"
          aria-label={ariaLabel}
          aria-expanded={open}
          aria-controls={listId}
          aria-haspopup="listbox"
          disabled={disabled}
          className={cn(TRIGGER_CLASS, className)}
        >
          <span className="min-w-0 flex-1 truncate">{display}</span>
          <Icon name="chevrons-up-down" className="text-fg-subtle" />
        </button>
      </PopoverPrimitive.Trigger>
      <PopoverPrimitive.Portal>
        <PopoverPrimitive.Content
          align="start"
          sideOffset={4}
          className="z-50 w-[var(--radix-popover-trigger-width)] min-w-56 overflow-hidden rounded-lg border border-line bg-surface text-fg shadow-pop outline-none animate-fade-in motion-reduce:animate-none"
        >
          <Command loop>
            <div className="flex items-center gap-2 border-b border-line px-3">
              <Icon name="search" className="text-fg-subtle" />
              <Command.Input aria-label={labels.search} placeholder={labels.search} className="h-10 flex-1 bg-transparent text-sm text-fg outline-none placeholder:text-fg-subtle" />
            </div>
            <Command.List id={listId} className="max-h-64 overflow-y-auto p-1">
              <Command.Empty className="px-3 py-6 text-center text-sm text-fg-muted">{labels.empty}</Command.Empty>
              {children}
            </Command.List>
          </Command>
        </PopoverPrimitive.Content>
      </PopoverPrimitive.Portal>
    </PopoverPrimitive.Root>
  );
}

function OptionBody({ option, selected }: { option: ComboboxOption; selected: boolean }) {
  return (
    <>
      <Icon name="check" className={cn("size-3.5 text-primary-ink", !selected && "invisible")} />
      <span className="min-w-0 flex-1">
        <span className="block truncate">
          {option.label}
          {selected ? <span className="sr-only"> (selected)</span> : null}
        </span>
        {option.description ? <span className="block truncate text-[11px] text-fg-subtle">{option.description}</span> : null}
      </span>
    </>
  );
}

export interface ComboboxProps<T extends string> extends PickerProps<T> {
  value: T | null;
  onValueChange: (value: T) => void;
}

/** Searchable single select (Radix Popover + cmdk). */
export function Combobox<T extends string>({ options, value, onValueChange, labels, ...rest }: ComboboxProps<T>) {
  const [open, setOpen] = useState(false);
  const merged = { ...DEFAULT_LABELS, ...labels };
  const current = options.find((o) => o.value === value);
  return (
    <PickerShell
      id={rest.id}
      ariaLabel={rest["aria-label"]}
      disabled={rest.disabled}
      className={rest.className}
      labels={merged}
      open={open}
      onOpenChange={setOpen}
      display={current ? current.label : <span className="text-fg-subtle">{merged.placeholder}</span>}
    >
      {options.map((option) => (
        <Command.Item
          key={option.value}
          value={option.value}
          keywords={[option.label, ...(option.keywords ?? [])]}
          onSelect={() => {
            onValueChange(option.value);
            setOpen(false);
          }}
          className={ITEM_CLASS}
        >
          <OptionBody option={option} selected={option.value === value} />
        </Command.Item>
      ))}
    </PickerShell>
  );
}

export interface MultiSelectProps<T extends string> extends PickerProps<T> {
  value: readonly T[];
  onValueChange: (value: T[]) => void;
  /** Renders the summary when several are chosen, e.g. (n) => `${n} selected`. */
  summary?: (count: number) => string;
}

/** Searchable multi select; the popover stays open while toggling. */
export function MultiSelect<T extends string>({ options, value, onValueChange, labels, summary, ...rest }: MultiSelectProps<T>) {
  const [open, setOpen] = useState(false);
  const merged = { ...DEFAULT_LABELS, ...labels };
  const chosen = options.filter((o) => value.includes(o.value));
  const toggle = (v: T) => {
    onValueChange(value.includes(v) ? value.filter((x) => x !== v) : [...value, v]);
  };
  const display =
    chosen.length === 0 ? (
      <span className="text-fg-subtle">{merged.placeholder}</span>
    ) : chosen.length <= 2 || !summary ? (
      chosen.map((o) => o.label).join(", ")
    ) : (
      summary(chosen.length)
    );
  return (
    <PickerShell
      id={rest.id}
      ariaLabel={rest["aria-label"]}
      disabled={rest.disabled}
      className={rest.className}
      labels={merged}
      open={open}
      onOpenChange={setOpen}
      display={display}
    >
      {options.map((option) => {
        const selected = value.includes(option.value);
        return (
          <Command.Item
            key={option.value}
            value={option.value}
            keywords={[option.label, ...(option.keywords ?? [])]}
            onSelect={() => {
              toggle(option.value);
            }}
            className={ITEM_CLASS}
          >
            <OptionBody option={option} selected={selected} />
          </Command.Item>
        );
      })}
    </PickerShell>
  );
}
